// UK (Europe/London) time helpers for booking slots.
//
// The booking system is UK-based and the availability picker shows UK
// wall-clock slots, so a slot of "14:00" must ALWAYS mean 14:00 in the UK —
// never 14:00 in whatever timezone the customer's browser happens to be in.
// Building the booking instant with the browser's local `setHours()` shifted
// bookings made from non-UK browsers by the browser↔UK offset; the checkout's
// slot re-validation then rejected them ("professional no longer available" →
// "Could not start checkout"). These helpers anchor to UK time regardless of
// the browser's zone.

/**
 * London's UTC offset in minutes at the given instant: +60 during BST,
 * 0 during GMT. Computed via Intl so it's correct on any browser.
 */
function londonOffsetMinutes(utcMs: number): number {
  const d = new Date(utcMs);
  const asUtc = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' }));
  const asLondon = new Date(
    d.toLocaleString('en-US', { timeZone: 'Europe/London' }),
  );
  return Math.round((asLondon.getTime() - asUtc.getTime()) / 60000);
}

/**
 * Build the ISO instant for a chosen booking slot, interpreting `hhmm`
 * ("HH:MM") as UK (Europe/London) wall-clock — independent of the browser's
 * timezone. The calendar Y/M/D comes from `date`'s local components (the day
 * the customer picked); the time comes from `hhmm`.
 *
 * For a UK browser this returns the exact same instant the old
 * `setHours()`-based code did, so it's a no-op there — it only differs
 * (correctly) when the browser isn't on UK time.
 */
export function ukSlotToISO(date: Date, hhmm: string): string {
  const [hhRaw, mmRaw] = (hhmm || '').split(':');
  const hh = parseInt(hhRaw, 10);
  const mm = parseInt(mmRaw, 10);
  const h = Number.isFinite(hh) ? hh : 0;
  const m = Number.isFinite(mm) ? mm : 0;
  const naiveUtc = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    h,
    m,
    0,
    0,
  );
  return new Date(naiveUtc - londonOffsetMinutes(naiveUtc) * 60000).toISOString();
}

/**
 * Inverse of ukSlotToISO: read an ISO instant back as UK (Europe/London)
 * wall-clock parts (calendar day + HH:MM). Restore a saved booking time from
 * draft.scheduled_start with THIS, not `new Date(iso).getHours()` — the latter
 * uses the browser's zone, so on a non-UK browser a slot saved as "14:00 UK"
 * reads back shifted (e.g. "18:30" in IST), never matches the UK slot list,
 * and gets silently dropped — bouncing the customer back to "Pick a time" at
 * checkout. Computed via Intl so it's correct on any browser.
 */
export function isoToUkParts(iso: string): {
  year: number;
  month: number; // 1-12
  day: number;
  hh: number;
  mm: number;
} {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string): string =>
    parts.find((p) => p.type === t)?.value ?? '';
  let hh = parseInt(get('hour'), 10);
  if (hh === 24) hh = 0; // some engines emit "24" for UK midnight
  return {
    year: parseInt(get('year'), 10),
    month: parseInt(get('month'), 10),
    day: parseInt(get('day'), 10),
    hh: Number.isFinite(hh) ? hh : 0,
    mm: parseInt(get('minute'), 10) || 0,
  };
}

/** "HH:MM" UK (Europe/London) wall-clock for an ISO instant. */
export function isoToUkHm(iso: string): string {
  const { hh, mm } = isoToUkParts(iso);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * A `Date` whose LOCAL Y/M/D equals the UK calendar day of `iso`. The booking
 * calendar reads date.getFullYear()/getMonth()/getDate() (and feeds them back
 * into ukSlotToISO), so the day must be the UK day regardless of browser zone.
 */
export function isoToUkDateOnly(iso: string): Date {
  const { year, month, day } = isoToUkParts(iso);
  return new Date(year, month - 1, day);
}

/**
 * "Now" expressed in UK (Europe/London) terms: the calendar day (YYYY-MM-DD)
 * and the minute-of-day (0–1439). Use this for the same-day lead-time gate so
 * the cutoff is anchored to UK time — computing it from the browser-local
 * `new Date()` shifts it by the browser↔UK offset, which on a browser AHEAD of
 * the UK (e.g. IST, +4.5h) pushes the cutoff hours too late and wrongly hides
 * every same-day slot ("no same-day slots — needs 2 hours' notice").
 */
export function ukNow(): { ymd: string; minutesOfDay: number } {
  const now = new Date();
  const ymd = now.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (t: string): string =>
    parts.find((p) => p.type === t)?.value ?? '0';
  let hh = parseInt(get('hour'), 10);
  if (hh === 24) hh = 0;
  const mm = parseInt(get('minute'), 10) || 0;
  return { ymd, minutesOfDay: hh * 60 + mm };
}
